import { CARD_GROUPS, CARD_DEFAULTS } from '../config/dashboardConfig';
import { getCardUuid, getCardTypeByUuid } from './cardIdMapper';

/**
 * 대시보드 레이아웃을 API 형식으로 변환
 * @param {Object} layout - 대시보드 레이아웃 객체
 * @param {string} userSysId - 사용자 시스템 ID
 * @returns {Object} API 요청 payload
 */
export const layoutToApiFormat = (layout, userSysId) => {
  const cardWidgetPositions = [];
  
  // 활성화된 카드만 위치 정보에 포함 (비활성화 그룹 제외)
  Object.keys(layout).forEach((group) => {
    if (group === CARD_GROUPS.DISABLED_COMMON || group === CARD_GROUPS.DISABLED_DR_RELATED) return;
    
    const cards = layout[group] || [];
    cards.forEach((card) => {
      // 활성화된 카드만 포함
      if (card.enabled) {
        // CARD_ID_TO_UUID_MAP에서 고정된 UUID 가져오기
        const uuid = getCardUuid(card.type);
        if (!uuid) {
          console.warn(`[layoutToApiFormat] UUID를 찾을 수 없습니다: card.type=${card.type}`);
          return;
        }
        
        cardWidgetPositions.push({
          cardWidgetPositionSysId: uuid, // CARD_ID_TO_UUID_MAP의 고정 UUID 사용
          positionX: card.x || 1,
          positionY: card.y || 1,
        });
        
      }
    });
  });
  
  
  return {
    userSysId,
    cardWidgetPositions,
  };
};

/**
 * API 응답을 대시보드 레이아웃 형식으로 변환
 * API 응답에는 UUID와 위치 정보만 있으므로, UUID를 카드 타입으로 변환하고
 * CARD_DEFAULTS에서 기본 정보(그룹, 크기 등)를 가져와서 레이아웃을 구성합니다.
 * 
 * @param {Object} apiResponse - API 응답 객체 { Total: number, Items: Array }
 * @param {Object} defaultLayout - 기본 레이아웃 (API 응답이 없을 때 사용)
 * @returns {Object} 대시보드 레이아웃 객체
 */
export const apiFormatToLayout = (apiResponse, defaultLayout) => {
  // 기본 레이아웃 구조 초기화
  const layout = {
    [CARD_GROUPS.COMMON]: [],
    [CARD_GROUPS.DR_RELATED]: [],
    [CARD_GROUPS.DISABLED_COMMON]: [],
    [CARD_GROUPS.DISABLED_DR_RELATED]: [],
  };
  
  // API 응답이 없거나 Items가 없으면 기본 레이아웃 반환
  if (!apiResponse?.Items || apiResponse.Items.length === 0) {
    return defaultLayout || layout;
  }
  
  // API 응답에서 UUID 목록 추출
  const apiUuids = new Set(apiResponse.Items.map(item => item.CardWidgetPositionSysId));
  
  // 모든 카드 타입 순회
  Object.keys(CARD_DEFAULTS).forEach((cardType) => {
    const cardDefault = CARD_DEFAULTS[cardType];
    const cardUuid = getCardUuid(cardType);
    
    // API 응답에 해당 UUID가 있는지 확인
    const apiItem = apiResponse.Items.find(item => item.CardWidgetPositionSysId === cardUuid);
    
    if (apiItem) {
      // API에 위치 정보가 있으면 활성화된 카드로 추가
      const card = {
        ...cardDefault,
        enabled: true,
        x: apiItem.PositionX,
        y: apiItem.PositionY,
      };
      
      // 해당 그룹에 추가
      const targetGroup = cardDefault.group || CARD_GROUPS.COMMON;
      if (!layout[targetGroup]) {
        layout[targetGroup] = [];
      }
      layout[targetGroup].push(card);
    } else {
      // API에 위치 정보가 없으면 비활성화된 카드로 추가
      // 원래 그룹에 따라 적절한 비활성화 그룹으로 배치
      const originalGroup = cardDefault.group || CARD_GROUPS.COMMON;
      const targetDisabledGroup = originalGroup === CARD_GROUPS.COMMON 
        ? CARD_GROUPS.DISABLED_COMMON 
        : CARD_GROUPS.DISABLED_DR_RELATED;
      
      const card = {
        ...cardDefault,
        enabled: false,
        group: targetDisabledGroup,
      };
      layout[targetDisabledGroup].push(card);
    }
  });
  
  // 비활성화 그룹들의 카드에 순차적 위치 할당
  [CARD_GROUPS.DISABLED_COMMON, CARD_GROUPS.DISABLED_DR_RELATED].forEach((disabledGroup) => {
    if (layout[disabledGroup] && layout[disabledGroup].length > 0) {
      layout[disabledGroup].forEach((card, index) => {
        const col = (index % 4) + 1;
        const row = Math.floor(index / 4) + 1;
        card.x = col;
        card.y = row;
      });
    }
  });
  
  // 각 그룹별로 y, x 기준 정렬
  Object.keys(layout).forEach((group) => {
    if (layout[group].length > 0) {
      layout[group].sort((a, b) => {
        if (a.y !== b.y) return (a.y || 0) - (b.y || 0);
        return (a.x || 0) - (b.x || 0);
      });
    }
  });
  
  return layout;
};

